import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionItemIndicator,
  Badge,
  Box,
  Card,
  Circle,
  Container,
  Flex,
  Heading,
  HStack,
  Icon,
  Image,
  SimpleGrid,
  Square,
  Stack,
  Text,
  VStack
} from '@chakra-ui/react';
import { NextSeo } from 'next-seo';
import { Navbar } from '@/components/chakraUIPro/Navbar';
import { Footer } from '@/components/chakraUIPro/Footer';
import FAQs from '@/components/FAQs';
import Loading from '@/components/Loading';
import { Button } from '@/components/ui/button';
import NextLink from 'next/link';
import { LuArrowRight, LuBrainCircuit, LuChevronRight, LuCombine, LuRocket, LuWorkflow } from 'react-icons/lu';
import { RiGlobalLine, RiTranslateAi } from 'react-icons/ri';
import { PiArrowBendDoubleUpRightBold } from 'react-icons/pi';
import { IoShieldCheckmarkOutline } from 'react-icons/io5';
import { SectionHeader } from '@/components/chakraUIPro/SectionHeader';
import { ImagePlaceholder } from '@/components/chakraUIPro/image-placeholder';
import { HiOutlineHandRaised } from 'react-icons/hi2';
import { BsFiletypeCsv } from 'react-icons/bs';
import { AiOutlineApi } from 'react-icons/ai';
import { AccordionItem, AccordionItemContent, AccordionItemTrigger, AccordionRoot } from '@/components/ui/accordion';
import Link from '@/components/ui/link';
import { BiSupport } from 'react-icons/bi';
import { MdCurrencyExchange } from 'react-icons/md';
import { VscBracketError } from 'react-icons/vsc';
import { motion, useInView } from 'framer-motion';
import { HeroMain } from '@/components/LandingPage/HeroMain';
import { ContactUs } from '@/components/LandingPage/ContactUs';
import { AppRouteLoadingShell } from '@/components/Routing/AppRouteLoadingShell';
import { useStaticExportRedirect } from '@/hooks/useStaticExportRedirect';

// const MotionBox = motion.div;

const FeatureCard1 = (props: {
  feature: {
    icon: React.ElementType;
    title: string;
    description: string;
    url?: string;
  };
}) => (
  <Stack gap='3'>
    <Icon as={props.feature.icon} color='accent' size='lg' />
    <Text fontWeight='medium'>{props.feature.title}</Text>
    <Text color='fg.muted' mb='3'>
      {props.feature.description}
    </Text>
    {/*<Link textStyle='sm' fontWeight='medium' href={feature.url}>
                      Learn more <LuArrowRight />
                    </Link>*/}
  </Stack>
);

const Feature1 = (props: {
  platformFeatures: {
    icon: React.ElementType;
    title: string;
    description: string;
    url?: string;
  }[];
  callbackfn: (feature) => React.ReactElement;
}) => {
  const { t } = useTranslation('landing');
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Container variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1, // Time between each card animation
        delayChildren: 0.2 // Initial delay before first card
      }
    }
  };

  // Individual card variants
  const cardVariants = {
    hidden: {
      y: 40,
      opacity: 0,
      scale: 0.98
    },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1] // Subtle spring-like curve
      }
    }
  };

  return (
    <Container maxW='8xl' mb={{ base: 20, md: 40 }}>
      <Flex direction={{ base: 'column', md: 'row' }} gap={{ base: '8', md: '20' }}>
        <Stack gap={{ base: '2', md: '3' }} pt='8' maxW='md' w='full'>
          <Text textStyle='xs' textTransform='uppercase' fontWeight='semibold' letterSpacing='wide'>
            {t('hero.featuresThatMatter')}
          </Text>
          <Heading as='h2' textStyle={{ base: '4xl', md: '5xl' }}>
            {t('hero.platformTitle')}
          </Heading>
          <Text color='fg.muted' textStyle='lg' my='2'>
            {t('hero.platformDescription')}
          </Text>
          {/*<Button alignSelf='flex-start'>
                  Learn more
                  <LuArrowRight />
                </Button>*/}
        </Stack>
        <Box w='full' flex='1'>
          <Image src='/images/v2-screenshots.png' alt='eezi app' width='full' />
        </Box>
      </Flex>

      <motion.div
        // className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
        initial={mounted ? 'hidden' : false}
        animate={mounted && isInView ? 'visible' : 'hidden'}
        variants={containerVariants}
      >
        <SimpleGrid columns={{ base: 2, md: 3 }} gap='10' py='12' ref={ref}>
          {props.platformFeatures.map((feature, index) => (
            <motion.div key={index} className='bg-white rounded-lg shadow-md p-6' variants={cardVariants}>
              {props.callbackfn(feature)}
            </motion.div>
          ))}
        </SimpleGrid>
      </motion.div>
    </Container>
  );
};

const Index = () => {
  const router = useRouter();
  const [selected, setSelected] = useState<string[]>(['manual']);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const { t } = useTranslation('landing');
  const { isAppRedirect, isRedirecting } = useStaticExportRedirect(mounted);
  const authCallbackPath = router.query.code ? `/auth/callback${router.asPath.slice(1)}` : null;
  const features = [
    {
      icon: RiGlobalLine,
      title: t('features.globalCompliance.title'),
      description: t('features.globalCompliance.description')
    },
    {
      icon: PiArrowBendDoubleUpRightBold,
      title: t('features.seamlessIntegration.title'),
      description: t('features.seamlessIntegration.description')
    },
    {
      icon: IoShieldCheckmarkOutline,
      title: t('features.simplifiedCompliance.title'),
      description: t('features.simplifiedCompliance.description')
    }
  ];
  const platformFeatures = [
    {
      icon: MdCurrencyExchange,
      title: t('platform.currencyConversion.title'),
      description: t('platform.currencyConversion.description'),
      url: '#'
    },
    {
      icon: RiTranslateAi,
      title: t('platform.languageBarriers.title'),
      description: t('platform.languageBarriers.description'),
      url: '#'
    },
    {
      icon: LuBrainCircuit,
      title: t('platform.ai.title'),
      description: t('platform.ai.description'),
      url: '#'
    },
    {
      icon: LuCombine,
      title: t('platform.singleFormat.title'),
      description: t('platform.singleFormat.description'),
      url: '#'
    },
    {
      icon: LuWorkflow,
      title: t('platform.preserveWorkflow.title'),
      description: t('platform.preserveWorkflow.description'),
      url: '#'
    },
    {
      icon: VscBracketError,
      title: t('platform.errorRecovery.title'),
      description: t('platform.errorRecovery.description'),
      url: '#'
    }
  ];
  const ingestionFeatures = [
    {
      value: 'manual',
      icon: HiOutlineHandRaised,
      title: t('ingestion.adhoc.title'),
      description: t('ingestion.adhoc.description'),
      image: <ImagePlaceholder minH='sm' rounded='lg' />
    },
    {
      value: 'csv',
      icon: BsFiletypeCsv,
      title: t('ingestion.sftp.title'),
      description: t('ingestion.sftp.description'),
      url: '#',
      image: <ImagePlaceholder minH='sm' rounded='lg' />
    },
    {
      value: 'standard-api',
      icon: AiOutlineApi,
      title: t('ingestion.api.title'),
      description: t('ingestion.api.description'),
      url: '#',
      image: <ImagePlaceholder minH='sm' rounded='lg' />
    },
    {
      value: 'enterprise-api',
      icon: RiGlobalLine,
      title: t('ingestion.enterprise.title'),
      description: t('ingestion.enterprise.description'),
      url: '#',
      image: <ImagePlaceholder minH='sm' rounded='lg' />
    }
  ];
  // const selectedFeature = ingestionFeatures.find((feature) => feature.value === selected[0]);

  // Handle SSO authentication callback
  useEffect(() => {
    if (mounted && router.isReady && authCallbackPath) {
      // Redirect to auth callback handler to process the SSO code
      router.replace(authCallbackPath).catch((err) => {
        console.error('SSO callback redirect error', { err });
      });
    }
  }, [authCallbackPath, mounted, router.isReady, router.replace]);

  if (isRedirecting) {
    return isAppRedirect ? <AppRouteLoadingShell /> : <Loading />;
  }

  return (
    <>
      <div data-static-app-route-shell=''>
        <AppRouteLoadingShell />
      </div>
      <NextSeo
        title='eezi | Global E-Invoicing & e-VAT Compliance Platform'
        description='Simplify real-time e-invoicing and e-reporting for compliance across 60+ countries.
        One streamlined API keeps your invoices compliant worldwide.'
        canonical='https://eezi.io/'
        openGraph={{
          url: 'https://eezi.io/',
          title: 'eezi | Global E-Invoicing & e-VAT Compliance Platform',
          description:
            'Simplify real-time e-invoicing and e-reporting for compliance across 60+ countries.' +
            'One streamlined API keeps your invoices compliant worldwide.'
        }}
      />
      <Box data-static-marketing-route='' bg='bg-default' position='relative' pb={{ base: 10, md: 20 }}>
        <Navbar py={4} />
        {/* Hero */}
        <HeroMain />

        {/* Logos */}
        {/*<Container py='12' pb={20}>
          <Flex gap='12' direction={{ base: 'column', lg: 'row' }}>
            <Heading size='2xl' flex='1' maxW={{ lg: 'sm' }}>
              Join hundreds of happy VAT IT customers
            </Heading>
            <SimpleGrid columns={{ base: 2, md: 4 }} gap='8' flex='1'>
              {Object.entries(logos).map(([name, Logo]) => (
                <Logo monochrome height='32px' key={name} />
              ))}
            </SimpleGrid>
          </Flex>
        </Container>*/}
        {/* High-level Features */}
        <Box w='full' py={{ base: 10, md: 20 }}>
          <Container my={{ base: 10, md: 20 }}>
            <Stack gap='12'>
              <Flex justify='space-between' gap='4' direction={{ base: 'column', md: 'row' }}>
                <Stack flex='1' align='flex-start' gap='3'>
                  <Heading textStyle={{ base: '4xl', md: '5xl' }} w='full' fontWeight='bold' color='heading'>
                    {t('cta.purposeCrafted')}
                  </Heading>
                  <Text textStyle='lg'>{t('cta.description')}</Text>
                </Stack>
                <Button mt={12} asChild>
                  <NextLink href='/#contact-us'>
                    {t('cta.letsTalk')} <LuArrowRight />
                  </NextLink>
                </Button>
              </Flex>

              <SimpleGrid columns={{ base: 1, md: 3 }} gap='4'>
                {features.map((feature) => (
                  <Card.Root key={feature.title}>
                    <Card.Body gap={{ base: '4', md: '10' }}>
                      <Circle bgColor='accent' size={12}>
                        <Icon as={feature.icon} size='2xl' color='#fff' />
                      </Circle>
                      <Stack>
                        <Card.Title>{feature.title}</Card.Title>
                        <Card.Description>{feature.description}</Card.Description>
                      </Stack>
                    </Card.Body>
                  </Card.Root>
                ))}
              </SimpleGrid>
            </Stack>
          </Container>
        </Box>
        {/* Mandates */}
        <Container py={{ base: 10, md: 10 }} mb={{ base: 10, md: 10 }} maxW='5xl'>
          <Stack align='center' gap='8'>
            <VStack gap='3' textAlign='center'>
              <Badge size='lg' variant='surface' rounded='full' alignSelf='center'>
                {t('mandates.stayCompliant')}
              </Badge>
              <Heading as='h2' size='4xl'>
                {t('mandates.title')}
              </Heading>
              <Text color='fg.muted' textStyle={{ base: 'md', md: 'lg' }} maxW='3xl'>
                {t('mandates.description')}
              </Text>
            </VStack>
            <Button size='lg' mt={12} asChild>
              <NextLink href='/#contact-us'>{t('mandates.connectExpert')}</NextLink>
            </Button>
          </Stack>
        </Container>

        {/* Dark features block */}
        <Box py={{ base: 20, md: 40 }} bgColor='#212c38' w='full' mx={0} className='dark' color='white'>
          {/* Platform features */}
          <Feature1
            platformFeatures={platformFeatures}
            callbackfn={(feature) => <FeatureCard1 key={feature.title} feature={feature} />}
          />

          {/* Ingestion features */}
          <Container>
            <Flex direction={{ base: 'column', md: 'row' }} gap={{ base: '8', md: '20' }} mx='auto' w='full'>
              <Box flex='1' w='full'>
                <Image
                  src='/images/screenshot-onboarding.png'
                  alt='eezi website'
                  aspectRatio='1'
                  width='full'
                  borderRadius={{ base: '45px', md: '30px' }}
                  mb={{ base: 20, md: 0 }}
                />
              </Box>
              <Stack gap='6' pt='2' flex='1'>
                <SectionHeader
                  tagline={t('ingestionSection.title')}
                  headline={t('ingestionSection.subtitle')}
                  description={t('ingestionSection.description')}
                />
                <AccordionRoot variant='plain' size='lg' value={selected} onValueChange={(e) => setSelected(e.value)}>
                  {ingestionFeatures.map((feature) => (
                    <AccordionItem key={feature.title} value={feature.value}>
                      <AccordionItemTrigger
                        fontWeight='semibold'
                        alignItems='center'
                        _expanded={{ color: 'colorPalette.fg' }}
                      >
                        <HStack flex='1' color={selected[0] === feature.value ? 'accent' : 'white'}>
                          <Icon as={feature.icon} size='md' />
                          {feature.title}
                        </HStack>
                        <AccordionItemIndicator />
                      </AccordionItemTrigger>
                      <AccordionItemContent>
                        <Accordion.ItemBody>
                          <Stack gap='5'>
                            {feature.description}
                            {/*<Box flex='1' w='full' hideFrom='md'>
                              {selectedFeature?.image}
                            </Box>*/}
                          </Stack>
                        </Accordion.ItemBody>
                      </AccordionItemContent>
                    </AccordionItem>
                  ))}
                </AccordionRoot>
              </Stack>
            </Flex>
          </Container>
        </Box>

        {/* Global reach */}
        <Container py={{ base: '16', md: '24' }}>
          <VStack gap='10' textAlign='center'>
            <Stack gap='4'>
              <Badge size='lg' variant='surface' colorPalette='gray' alignSelf='center'>
                <Icon as={RiGlobalLine} size='sm' />
                {t('countries.title')}
              </Badge>
              <Heading
                as='h2'
                textStyle={{ base: '4xl', md: '6xl' }}
                maxW={{ md: '2xl' }}
                mx='auto'
                lineHeight='tighter'
              >
                {t('countries.globalReach')}
              </Heading>
              <Text color='fg.muted' textStyle={{ base: 'lg', md: 'xl' }} maxW={{ md: '4xl' }} mx='auto'>
                {t('countries.description')}
              </Text>
            </Stack>

            <Stack align='center' direction={{ base: 'column', md: 'row' }} gap='3'>
              <Button asChild size='xl'>
                <NextLink href='/sign-up/'>
                  {t('countries.getStarted')} <LuChevronRight />
                </NextLink>
              </Button>
              <Button asChild variant='ghost' size='xl'>
                <NextLink href='/#contact-us'>
                  {t('countries.needSpecific')} <LuChevronRight />
                </NextLink>
              </Button>
            </Stack>

            <Box maxW='4xl' mx='auto' w='full' mt='10'>
              <Image src='/images/v2-atlas.png' alt='eezi reach' width='full' />
            </Box>
            <Text color='fg.muted' textStyle={{ base: 'lg', md: 'xl' }} maxW={{ md: '4xl' }} mx='auto'>
              {t('countries.continuouslyUpdated')}
            </Text>
          </VStack>
        </Container>

        {/* CTA */}
        <Container maxW='8xl' py='20'>
          <Flex direction={{ base: 'column', md: 'row' }} gap={{ base: '8', md: '20' }} justify='space-between'>
            <Stack flex='1' maxW='2xl' align='flex-start'>
              <Heading as='h2' size='4xl'>
                {t('cta2.title')}
              </Heading>
              <Text color='fg.muted' textStyle='lg' mb='4'>
                {t('cta2.description')}
              </Text>
              {/*<Button size='lg'>Start Building</Button>*/}
            </Stack>

            <Flex direction={{ base: 'column', md: 'row' }} flex='1' gap={{ base: '8', md: '4' }} align='flex-start'>
              <Stack direction={{ base: 'row', md: 'column' }} gap='4'>
                <Square size='10' borderWidth='1px' rounded='l2'>
                  <Icon as={LuRocket} color='colorPalette.solid' />
                </Square>
                <Stack flex='1'>
                  <Text fontWeight='medium'>{t('cta2.startTitle')}</Text>
                  <Text color='fg.muted' textStyle='sm'>
                    {t('cta2.startDescription')}
                  </Text>

                  <Link href='/sign-up/' fontWeight='medium' textStyle='sm'>
                    {t('cta2.signUpNow')}
                    <Icon as={LuChevronRight} color='fg.muted' alignSelf='center' />
                  </Link>
                </Stack>
              </Stack>
              <Stack direction={{ base: 'row', md: 'column' }} gap='4'>
                <Square size='10' borderWidth='1px' rounded='l2'>
                  <Icon as={BiSupport} color='colorPalette.solid' />
                </Square>
                <Stack flex='1'>
                  <Text fontWeight='medium'>{t('cta2.demoTitle')}</Text>
                  <Text color='fg.muted' textStyle='sm'>
                    {t('cta2.demoDescription')}
                  </Text>

                  <Link asChild href='/#contact-us' fontWeight='medium' textStyle='sm'>
                    {t('cta2.bookDemo')}
                    <Icon as={LuChevronRight} color='fg.muted' alignSelf='center' />
                  </Link>
                </Stack>
              </Stack>
            </Flex>
          </Flex>
        </Container>

        {/*{router.asPath && !router.query.path && (
          <Box maxW='8xl' mx='auto' p={8} textAlign='left'>
            <HeroCard />
          </Box>
        )}*/}

        {/* Contact us */}
        <Box bgColor='gray.100' _dark={{ bgColor: '#0f1927' }} w='full' pt={20} mb={20}>
          <Box maxW='8xl' mx='auto' px={4}>
            <ContactUs />
          </Box>
        </Box>

        {/* FAQs */}
        <FAQs as='section' maxW='8xl' mx='auto' px={2} textAlign='left' pt={{ base: 6 }} overflow='hidden' id='faqs' />
      </Box>

      {/* Footer */}
      <Box data-static-marketing-route='' bg='bg-default'>
        <Box maxW='8xl' mx='auto' p={8} textAlign='left'>
          <Footer />
        </Box>
      </Box>
    </>
  );
};

export default Index;
